import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
//type
import type { ReactNode, ComponentProps } from 'react';
import type {
    ChildrenRenderProps,
    SelectComponentProps,
    OptionType
} from '@/components/base/Select/SelectComponent/types';

//styleConfig
import { optionsSizeStyle } from '@/components/base/Select/SelectComponent/selectConfig';

//components
import { DefaultSelectOption } from '@/components/base/Select/SelectComponent/SubComponents/SelectOption/DefaultOption';

type OptionContainerProps<TOption, TSelected> = Pick<
    SelectOptionProps<TOption, TSelected>,
    'size'
> &
    Omit<ComponentProps<typeof Listbox.Option>, 'value' | 'disabled'> & {
        option: TOption;
        children?: ChildrenRenderProps<TOption>;
    };

function OptionContainer<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    children,
    option,
    size = 'default',
    ...restProps
}: OptionContainerProps<TOption, TSelected>) {
    return (
        <Listbox.Option
            data-testid="select-option-container"
            id={option.label}
            value={option}
            disabled={option?.disabled ?? null}
            className={({ active, selected, disabled }) => {
                return clsx(
                    'relative cursor-pointer select-none rounded-md outline-none duration-200',
                    optionsSizeStyle[size],
                    {
                        'text-icoBlue': selected,
                        'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/30': active,
                        'pointer-events-none text-icoGray-400': disabled
                    }
                );
            }}
            {...restProps}>
            {({ active, selected, disabled }) => (
                <>{children({ active, selected, disabled, option })}</>
            )}
        </Listbox.Option>
    );
}

type OptionProps<TOption, TSelected> = Omit<SelectOptionProps<TOption, TSelected>, 'children'> & {
    children: ChildrenRenderProps<TOption>;
};

export function Option<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    options,
    isMultiple,
    selected,
    size,
    children,
    ...restProps
}: OptionProps<TOption, TSelected>) {
    const { t } = useTranslation('commons');
    if (Array.isArray(options) && !options.length) {
        return <div className="py-1 text-center">{t('select_no_options')}</div>;
    }

    return (
        <>
            {options?.map((option, id) => {
                const selectedOptions = selected;

                const isSelected = (selected: boolean) => {
                    if (isMultiple && Array.isArray(selectedOptions)) {
                        return Boolean(selectedOptions?.find((el) => el?.value === option?.value));
                    }
                    return selected;
                };

                return (
                    <OptionContainer key={id} option={option} size={size}>
                        {({ selected, disabled, active, option }) => {
                            if (!children) {
                                return (
                                    <DefaultSelectOption
                                        option={option}
                                        selected={isSelected(selected)}
                                        disabled={disabled}
                                        isMultiple={isMultiple}
                                        {...restProps}
                                    />
                                );
                            }
                            return (
                                <>
                                    {children({
                                        selected: isSelected(selected),
                                        option,
                                        disabled,
                                        active
                                    })}
                                </>
                            );
                        }}
                    </OptionContainer>
                );
            })}
        </>
    );
}

export type SelectOptionProps<TOption, TSelected> = Pick<
    SelectComponentProps<TOption, TSelected>,
    'options' | 'selected'
> & {
    isTruncate: SelectComponentProps<TOption, TSelected>['panelOptions']['isTruncate'];
    forcedDisplay: SelectComponentProps<TOption, TSelected>['layoutOptions']['forcedDisplay'];
    isMultiple: SelectComponentProps<TOption, TSelected>['layoutOptions']['isMultiple'];
    size: SelectComponentProps<TOption, TSelected>['panelOptions']['size'];
    children: ChildrenRenderProps<TOption> | ReactNode;
};

export function SelectOption<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    options,
    isMultiple,
    selected,
    children,
    size,
    isTruncate,
    forcedDisplay
}: SelectOptionProps<TOption, TSelected>) {
    if (options) {
        return (
            <Option
                size={size}
                options={options}
                isMultiple={isMultiple}
                selected={selected}
                isTruncate={isTruncate}
                forcedDisplay={forcedDisplay}>
                {children as ChildrenRenderProps<TOption>}
            </Option>
        );
    }
    return children as ReactNode;
}
